import { useModalControl } from "../../customHooks/useModalControl";
import { formatToCurrency } from "../../helperFunctions";
import { useToggleGuarantorApprovalMutation } from "../../redux/features/loans/loans.api";
import { LoanGuaranteeData } from "../../redux/features/loans/loans.type";
import { Button } from "../buttons/button";
import { Icon } from "../icons/icon";
import { ConfirmationModal } from "../modals/app_modals/confirmationModal";
import { SuccessModal } from "../modals/app_modals/successModal";
import { MemberProfilePic } from "../profile/memberPicture";
import { ProfilePicture } from "../profile/profilePicture";
import { doNotification } from "../toast/nofitication";
import { ContainerCard } from "./containerCard";
import { InformationCard } from "./infoCard";

export const LoanInvitationCard = ({
  invitation,
  successCallback = () => {},
}: {
  invitation: LoanGuaranteeData;
  successCallback?: () => void;
}) => {
  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const [toggleApproval, { isLoading }] = useToggleGuarantorApprovalMutation();

  return (
    <>
      <ContainerCard className="!px-6 md:!px-8 !py-6 !max-w-[526px]">
        <div className="flex items-center gap-2">
          <ProfilePicture className="w-[32px] h-[32px]" tempSrc={""} />

          <Icon svg="guarantee-icon" width={20} height={20} className="" />

          <MemberProfilePic
            member={invitation?.GuarantorMemberID?.User}
            className="!w-[32px] !h-[32px] !text-[12px]"
          />
        </div>

        <p className="mt-4 text-base">
          <span className="text-pry-green-200 font-medium">
            {`${invitation?.MembershipLoanRequestID?.MemberID?.User?.FirstName}
          ${invitation?.MembershipLoanRequestID?.MemberID?.User?.LastName}`}
          </span>{" "}
          has requested you to stand as guarantor for a loan
        </p>

        <div className="mt-4 text-sm">
          <ul className="list-disc leading-6 list-inside">
            <li>
              Loan duration:{" "}
              <span className="text-pry-green-200 font-medium">
                {invitation?.MembershipLoanRequestID?.Tenure} months
              </span>
            </li>
            <li>
              Loan amount:{" "}
              <span className="text-pry-green-200 font-medium">
                {formatToCurrency(
                  invitation?.MembershipLoanRequestID?.Amount ?? 0,
                  { currency: "NGN", decimalPlaces: 2 }
                )}
              </span>
            </li>
            <li>
              Amount to guarantee:{" "}
              <span className="text-pry-green-200 font-medium">
                {formatToCurrency(invitation?.AmountGuarantee ?? 0, {
                  currency: "NGN",
                  decimalPlaces: 2,
                })}
              </span>
            </li>
          </ul>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <Button
            text="Decline"
            onClick={async () => {
              const resp = await toggleApproval({
                coop: invitation?.MembershipLoanRequestID?.Cooperative,
                isaccept: false,
                loanid: invitation?.MembershipLoanRequestID?.id,
              });

              if (resp.data?.success) {
                doNotification("Request denied successfully", "success");
                successCallback();
              }
            }}
            type="button"
            className="md:!py-2"
            loading={isLoading}
            disabled={isLoading}
            variation="secondary-light-2"
          />

          <Button
            text="I consent"
            onClick={() => {
              handleModalOpen("confirmationModal");
            }}
            type="button"
            className="md:!py-2"
            loading={false}
            disabled={false}
            variation="primary"
          />
        </div>
      </ContainerCard>

      <ConfirmationModal
        isModalOpen={modalOpen && modal === "confirmationModal"}
        onModalClose={handleModalClose}
        title="Final confirmation"
        subtitle={""}
        children={
          <div>
            <InformationCard
              info={
                <span>
                  Please note that{" "}
                  <span className="font-bold">
                    {formatToCurrency(invitation?.AmountGuarantee, {
                      decimalPlaces: 2,
                    })}
                  </span>{" "}
                  will be locked from your contributions as the amount you
                  guaranteed for this loan
                </span>
              }
              size="mini"
              variant="purple"
            />

            <div className="mt-3 text-sm">
              By proceeding, you agree and consent to be a guarantor for{" "}
              <span className="text-pry-green-200">{`${invitation?.MembershipLoanRequestID?.MemberID?.User?.FirstName}
                ${invitation?.MembershipLoanRequestID?.MemberID?.User?.LastName}`}</span>
            </div>
          </div>
        }
        onProceed={async () => {
          const resp = await toggleApproval({
            coop: invitation?.MembershipLoanRequestID?.Cooperative!,
            isaccept: true,
            loanid: invitation?.MembershipLoanRequestID?.id!,
          });

          if (resp.data?.success) {
            handleModalOpen("approvalSuccessModal");
            successCallback();
          }
        }}
        loading={isLoading}
      />

      <SuccessModal
        message={`You’ve successfully consented`}
        isModalOpen={modalOpen && modal === "approvalSuccessModal"}
        onModalClose={handleModalClose}
      />
    </>
  );
};
